import React from 'react'
import Head from 'next/head'

export type OpenGraphImage = {
    url: string
    width?: number
    height?: number
    alt?: string
}

export type SeoProps = {
    title?: string
    defaultTitle?: string
    titleTemplate?: string
    description?: string
    canonical?: string
    noindex?: boolean
    openGraph?: {
        type?: string
        locale?: string
        url?: string
        site_name?: string
        title?: string
        description?: string
        images?: OpenGraphImage[]
    }
}

export function SeoHead(props: SeoProps) {
    const {
        title,
        defaultTitle,
        titleTemplate,
        description,
        canonical,
        noindex,
        openGraph,
    } = props

    const computedTitle =
        title && titleTemplate
            ? titleTemplate.replace('%s', title)
            : (title ?? defaultTitle)

    return (
        <Head>
            {computedTitle ? <title>{computedTitle}</title> : null}
            {description ? (
                <meta name="description" content={description} />
            ) : null}
            {canonical ? <link rel="canonical" href={canonical} /> : null}
            {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

            {openGraph?.type && (
                <meta property="og:type" content={openGraph.type} />
            )}
            {openGraph?.locale && (
                <meta property="og:locale" content={openGraph.locale} />
            )}
            {openGraph?.url && (
                <meta property="og:url" content={openGraph.url} />
            )}
            {openGraph?.site_name && (
                <meta property="og:site_name" content={openGraph.site_name} />
            )}
            {openGraph?.title && (
                <meta property="og:title" content={openGraph.title} />
            )}
            {openGraph?.description && (
                <meta
                    property="og:description"
                    content={openGraph.description}
                />
            )}

            {openGraph?.images?.map((image, index) => (
                <React.Fragment key={`og:image:${index}`}>
                    <meta property="og:image" content={image.url} />
                    {image.width && (
                        <meta
                            property="og:image:width"
                            content={image.width.toString()}
                        />
                    )}
                    {image.height && (
                        <meta
                            property="og:image:height"
                            content={image.height.toString()}
                        />
                    )}
                    {image.alt && (
                        <meta property="og:image:alt" content={image.alt} />
                    )}
                </React.Fragment>
            ))}
        </Head>
    )
}
